import React, { useState, useMemo } from "react";
import {
  Row,
  Col,
  Card,
  Typography,
  Divider,
  Tooltip,
  Space
} from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { Line } from "@ant-design/plots";
import moment from "moment";
import ViewTypeSelector from "../AdminThroughputCharts/ViewTypeSelector";
import DateRangePicker from "../../DateRangePicker/DateRangePicker";

const { Title } = Typography;
const DATE_FORMAT = "YYYY/MM/DD";

const ImageGenerationsChart = ({ chartStyle, dateRange, onDateChange }) => {
  const [viewType, setViewType] = useState("Daily");

  // Generate dummy data based on date range
  const generateDummyData = useMemo(() => {
    const data = [];
    const start = moment(dateRange.start);
    const end = moment(dateRange.end);
    const models = ['Banano Pro', 'Flux', 'Qwen'];
    
    for (let date = start.clone(); date.isSameOrBefore(end); date.add(1, 'days')) {
      const modelBreakdown = {};
      let total = 0;
      
      models.forEach(model => {
        let generations;
        // Different ranges for different models
        if (model === 'Banano Pro') {
          generations = Math.floor(Math.random() * 300) + 150; // 150-450
        } else if (model === 'Flux') {
          generations = Math.floor(Math.random() * 200) + 100; // 100-300
        } else {
          generations = Math.floor(Math.random() * 150) + 50; // 50-200
        }
        modelBreakdown[model] = generations;
        total += generations;
      });
      
      data.push({
        date: date.format('YYYY/MM/DD'),
        generations: total,
        breakdown: modelBreakdown
      });
    }
    
    return data;
  }, [dateRange]);

  // Process data based on view type
  const processedData = useMemo(() => {
    if (viewType === "Daily") {
      return generateDummyData;
    }

    if (viewType === "Weekly") {
      const weeklyData = new Map();
      
      generateDummyData.forEach((item) => {
        const itemDate = moment(item.date, 'YYYY/MM/DD');
        const weekStart = itemDate.clone().startOf('week');
        const weekEnd = itemDate.clone().endOf('week');
        const weekLabel = `${weekStart.format('MMM DD')} - ${weekEnd.format('MMM DD')}`;
        const weekKey = weekStart.format('YYYY/MM/DD');
        
        if (!weeklyData.has(weekKey)) {
          weeklyData.set(weekKey, {
            date: weekLabel,
            generations: 0,
            breakdown: { 'Banano Pro': 0, 'Flux': 0, 'Qwen': 0 },
            sortDate: weekStart.format('YYYY/MM/DD')
          });
        }
        const weekItem = weeklyData.get(weekKey);
        weekItem.generations += item.generations;
        Object.keys(item.breakdown).forEach(model => {
          weekItem.breakdown[model] += item.breakdown[model];
        });
      });
      
      return Array.from(weeklyData.values()).sort((a, b) => 
        moment(a.sortDate, 'YYYY/MM/DD').diff(moment(b.sortDate, 'YYYY/MM/DD'))
      );
    }

    if (viewType === "Monthly") {
      const monthlyData = new Map();
      
      generateDummyData.forEach((item) => {
        const itemDate = moment(item.date, 'YYYY/MM/DD');
        const monthStart = itemDate.clone().startOf('month');
        const monthLabel = monthStart.format('MMM YYYY');
        const monthKey = monthStart.format('YYYY/MM');
        
        if (!monthlyData.has(monthKey)) {
          monthlyData.set(monthKey, {
            date: monthLabel,
            generations: 0,
            breakdown: { 'Banano Pro': 0, 'Flux': 0, 'Qwen': 0 },
            sortDate: monthStart.format('YYYY/MM/DD')
          });
        }
        const monthItem = monthlyData.get(monthKey);
        monthItem.generations += item.generations;
        Object.keys(item.breakdown).forEach(model => {
          monthItem.breakdown[model] += item.breakdown[model];
        });
      });
      
      return Array.from(monthlyData.values()).sort((a, b) => 
        moment(a.sortDate, 'YYYY/MM/DD').diff(moment(b.sortDate, 'YYYY/MM/DD'))
      );
    }

    return generateDummyData;
  }, [generateDummyData, viewType]);

  const chartConfig = useMemo(() => {
    return {
      data: processedData,
      xField: 'date',
      yField: 'generations',
      smooth: true,
      animation: false,
      color: '#722ed1',
      point: {
        size: 4,
        shape: 'circle',
        style: {
          fill: '#722ed1',
          stroke: '#fff',
          lineWidth: 2,
        },
      },
      lineStyle: {
        lineWidth: 2,
      },
      xAxis: {
        label: {
          autoRotate: true,
          autoHide: true,
        },
      },
      yAxis: {
        label: {
          formatter: (v) => `${v}`,
        },
        title: {
          text: 'Generations',
        },
      },
      tooltip: {
        customContent: (title, items) => {
          if (!items || items.length === 0) return '';
          const data = items[0]?.data;
          if (!data) return '';
          
          return `
            <div style="padding: 8px 12px;">
              <div style="margin-bottom: 8px; font-weight: bold;">${data.date}</div>
              <div style="margin-bottom: 4px;">Total: <strong>${data.generations}</strong></div>
              <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #f0f0f0;">
                <div style="margin-bottom: 4px;">
                  <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: #722ed1; margin-right: 6px;"></span>
                  Banano Pro: <strong>${data.breakdown['Banano Pro']}</strong>
                </div>
                <div style="margin-bottom: 4px;">
                  <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: #1890ff; margin-right: 6px;"></span>
                  Flux: <strong>${data.breakdown['Flux']}</strong>
                </div>
                <div>
                  <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: #52c41a; margin-right: 6px;"></span>
                  Qwen: <strong>${data.breakdown['Qwen']}</strong>
                </div>
              </div>
            </div>
          `;
        },
      },
    };
  }, [processedData]);

  return (
    <Col span={24}>
      <Card style={chartStyle}>
        <Row justify="space-between" align="middle">
          <Title level={4}>
            Image Generations Count{' '}
            <Tooltip title="Daily, weekly, or monthly count of AI image generations across all users">
              <QuestionCircleOutlined style={{ color: '#8c8c8c' }} />
            </Tooltip>
          </Title>
          <Row gutter={12}>
            <Col>
              <Space>
                <DateRangePicker
                  start={dateRange.start}
                  end={dateRange.end}
                  date_format={DATE_FORMAT}
                  onCalChange={onDateChange}
                />
                <ViewTypeSelector
                  value={viewType}
                  onChange={setViewType}
                />
              </Space>
            </Col>
          </Row>
        </Row>

        <Divider style={{ margin: "16px 0" }} />
        <Line {...chartConfig} />
      </Card>
    </Col>
  );
};

export default ImageGenerationsChart;
